import { Button } from '@trellis/roots/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@trellis/roots/command'
import { Popover, PopoverContent, PopoverTrigger } from '@trellis/roots/popover'
import { UNIT_TYPE_DEFAULTS } from '@trellis/shared'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useState } from 'react'

import { cn } from '@/lib/utils'

const DEFAULT_LABELS: Record<string, string> = {
  HOUR: 'Hour',
  DAY: 'Day',
  WEEK: 'Week',
  MONTH: 'Month',
  UNIT: 'Unit',
  FLAT: 'Flat',
}

function toDisplayLabel(value: string): string {
  return (
    DEFAULT_LABELS[value.toUpperCase()] ??
    value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
  )
}

interface UnitTypeComboboxProps {
  id?: string
  value: string
  onChange: (value: string) => void
}

export function UnitTypeCombobox({ id, value, onChange }: UnitTypeComboboxProps) {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')

  function commit(raw: string) {
    const normalised = raw.trim().toUpperCase()
    if (normalised.length > 0) onChange(normalised)
    setInputValue('')
    setOpen(false)
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && inputValue.trim().length > 0) {
      commit(inputValue)
      return
    }
    if (!nextOpen) setInputValue('')
    setOpen(nextOpen)
  }

  const upperInput = inputValue.trim().toUpperCase()
  const filteredDefaults = UNIT_TYPE_DEFAULTS.filter((type) => type.includes(upperInput))
  const isCustom =
    upperInput.length > 0 && !UNIT_TYPE_DEFAULTS.some((type) => type === upperInput)

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between rounded-md font-normal"
        >
          {toDisplayLabel(value)}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput
            className="text-base md:text-sm"
            placeholder="Search or type custom…"
            value={inputValue}
            onValueChange={setInputValue}
            onKeyDown={(keyboardEvent) => {
              if (keyboardEvent.key === 'Enter' && inputValue.trim().length > 0) {
                keyboardEvent.preventDefault()
                commit(inputValue)
              }
            }}
          />
          <CommandList>
            {isCustom && (
              <CommandGroup heading="Custom">
                <CommandItem value={upperInput} onSelect={() => commit(inputValue)}>
                  Use "{toDisplayLabel(inputValue.trim())}"
                </CommandItem>
              </CommandGroup>
            )}
            {filteredDefaults.length > 0 && (
              <CommandGroup heading="Defaults">
                {filteredDefaults.map((type) => (
                  <CommandItem key={type} value={type} onSelect={() => commit(type)}>
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value.toUpperCase() === type ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {DEFAULT_LABELS[type]}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {filteredDefaults.length === 0 && !isCustom && (
              <CommandEmpty>No matches.</CommandEmpty>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
